import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { client as apiClient } from '../../api/claims';
import { Toast, ToastMessage } from '../../components/ui';
import { LANE_IDS, LaneId } from '../../api/laneApi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import './lanes.css';
import '../Tasks.css';

/**
 * Team dashboard — Tasks Rebuild Task 14 (hard cutover). Extracted
 * verbatim-in-behaviour from the legacy Tasks.tsx "Dashboard" tab (cached
 * `/ui/team-tasks/dashboard` stats fetch, date-range filter, team table incl.
 * the AI row, refresh button, type-breakdown charts) into its own route so
 * `/tasks/dashboard` no longer redirects into the deleted legacy page.
 *
 * Admin-only: gated at the route level in App.tsx via `<AdminRoute>` (same
 * pattern as /audit-analysis, /archived-tasks, etc.) rather than a
 * self-check here — LaneShell's own Dashboard tab link is also already
 * `isAdmin`-gated, so a non-admin can't reach this via the UI either way.
 *
 * The legacy page's ad-hoc `message` state + inline banner is replaced with
 * the shared `Toast` primitive (Task 12) that every lane page already uses
 * for the same "surface one error/success line" job, rather than
 * reintroducing a second banner mechanism.
 */

const LANE_LABELS: Record<LaneId, string> = {
  client: 'Client',
  lender: 'Lender',
  post: 'Post',
  manual: 'Manual',
};

/** Same Post-lane exclusion as LaneShell's `VISIBLE_LANE_TABS` -- the
 * dedicated `/post-tasks` page remains the postal workflow, so this tab
 * row (a deliberate visual duplicate of LaneShell's) drops Post too. */
const VISIBLE_LANE_TABS: LaneId[] = LANE_IDS.filter((id) => id !== 'post');

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
  const [dashboardSort, setDashboardSort] = useState<'week' | 'month' | 'all'>('all');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [dateRangeApplied, setDateRangeApplied] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Load dashboard stats (cached server-side; see dashboard_stats_cache.py).
  const loadDashboardStats = (startDate?: string, endDate?: string) => {
    let url = '/ui/team-tasks/dashboard';
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`;
    }
    apiClient.get(url)
      .then(res => {
        setDashboardStats((res.data as any));
      })
      .catch(err => {
        console.error('[Dashboard] Failed to load stats:', err);
        setToast({ type: 'error', message: 'Failed to load dashboard stats' });
      });
  };

  useEffect(() => {
    if (dateRangeApplied && dateRangeStart && dateRangeEnd) {
      loadDashboardStats(dateRangeStart, dateRangeEnd);
    } else {
      loadDashboardStats();
    }
    // Mount-only: date range re-fetches are triggered explicitly by
    // applyDateRange/clearDateRange below, same as the legacy tab did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply date range filter
  const applyDateRange = () => {
    if (dateRangeStart && dateRangeEnd) {
      setDateRangeApplied(true);
      loadDashboardStats(dateRangeStart, dateRangeEnd);
    }
  };

  // Clear date range filter
  const clearDateRange = () => {
    setDateRangeStart('');
    setDateRangeEnd('');
    setDateRangeApplied(false);
    loadDashboardStats();
  };

  // Force-refresh dashboard stats (recomputes cache)
  const refreshDashboardStats = async () => {
    setDashboardRefreshing(true);
    try {
      const r = await apiClient.post('/ui/team-tasks/dashboard/refresh');
      setDashboardStats(r.data);
    } catch (err) {
      console.error('[Dashboard] Failed to refresh stats:', err);
      setToast({ type: 'error', message: 'Failed to refresh dashboard stats' });
    } finally {
      setDashboardRefreshing(false);
    }
  };

  return (
    <div className="tasks-container">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Same tab row LaneShell renders (lane links + Dashboard), so this
          page reads as part of the same shell rather than a separate app. */}
      <div className="lane-shell__tabs" role="tablist" aria-label="Task lanes">
        {VISIBLE_LANE_TABS.map((id) => (
          <Link key={id} to={`/tasks/${id}`} role="tab" aria-selected={false} className="lane-shell__tab">
            {LANE_LABELS[id]}
          </Link>
        ))}
        <Link
          to="/tasks/dashboard"
          role="tab"
          aria-selected="true"
          className="lane-shell__tab lane-shell__tab--active lane-shell__tab--dashboard"
        >
          Dashboard
        </Link>
      </div>

      {!dashboardStats && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading dashboard...</div>
      )}
      {dashboardStats && (
        <div className="dashboard-container">
          {/* Date range filter */}
          <div style={{ marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, color: '#374151' }}>Filter by date range:</span>
              <input
                type="date"
                value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
              <span style={{ color: '#6b7280' }}>to</span>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
              <button
                onClick={applyDateRange}
                disabled={!dateRangeStart || !dateRangeEnd}
                style={{
                  padding: '6px 16px', background: dateRangeStart && dateRangeEnd ? '#01B8C0' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '4px', cursor: dateRangeStart && dateRangeEnd ? 'pointer' : 'not-allowed',
                  fontWeight: 500
                }}
              >
                Apply
              </button>
              {dateRangeApplied && (
                <button
                  onClick={clearDateRange}
                  style={{
                    padding: '6px 16px', background: '#ef4444', color: 'white', border: 'none',
                    borderRadius: '4px', cursor: 'pointer', fontWeight: 500
                  }}
                >
                  Clear
                </button>
              )}
              {dateRangeApplied && dashboardStats.dateRange && (
                <span style={{ marginLeft: '8px', color: '#059669', fontWeight: 500 }}>
                  Showing: {dashboardStats.dateRange.start.split('-').reverse().join('/')} to {dashboardStats.dateRange.end.split('-').reverse().join('/')}
                </span>
              )}
              {!dateRangeApplied && dashboardStats?.lastUpdated && (
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Last updated {dashboardStats.lastUpdated.slice(11, 16)} UTC</span>
                  <button
                    className="refresh-btn"
                    onClick={refreshDashboardStats}
                    disabled={dashboardRefreshing}
                  >
                    {dashboardRefreshing ? 'Refreshing…' : '↻ Refresh'}
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* Team table */}
          <div className="tasks-table-container" style={{ marginTop: '16px' }}>
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>Team Member</th>
                  <th style={{ textAlign: 'center' }}>Pending</th>
                  <th style={{ textAlign: 'center' }}>Assigned to Claim</th>
                  {dateRangeApplied ? (
                    <th style={{ textAlign: 'center', background: '#dcfce7' }}>
                      Completed ({dashboardStats.dateRange?.start?.split('-').reverse().join('/')} to {dashboardStats.dateRange?.end?.split('-').reverse().join('/')})
                    </th>
                  ) : (
                    <>
                      <th
                        style={{ textAlign: 'center', cursor: 'pointer', background: dashboardSort === 'week' ? '#e0f2fe' : undefined }}
                        onClick={() => setDashboardSort('week')}
                        title="Click to sort by this column"
                      >
                        Completed (Week) {dashboardSort === 'week' && '▼'}
                      </th>
                      <th
                        style={{ textAlign: 'center', cursor: 'pointer', background: dashboardSort === 'month' ? '#e0f2fe' : undefined }}
                        onClick={() => setDashboardSort('month')}
                        title="Click to sort by this column"
                      >
                        Completed (Month) {dashboardSort === 'month' && '▼'}
                      </th>
                      <th
                        style={{ textAlign: 'center', cursor: 'pointer', background: dashboardSort === 'all' ? '#e0f2fe' : undefined }}
                        onClick={() => setDashboardSort('all')}
                        title="Click to sort by this column"
                      >
                        Completed (All Time) {dashboardSort === 'all' && '▼'}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {dashboardStats.stats.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      No team activity yet.
                    </td>
                  </tr>
                ) : [...dashboardStats.stats]
                  .sort((a: any, b: any) => {
                    if (dateRangeApplied) {
                      const diff = (b.completedInRange || 0) - (a.completedInRange || 0);
                      return diff !== 0 ? diff : a.name.localeCompare(b.name);
                    }
                    const field = dashboardSort === 'week' ? 'completedWeek'
                      : dashboardSort === 'month' ? 'completedMonth'
                      : 'completedAllTime';
                    const diff = b[field] - a[field];
                    return diff !== 0 ? diff : a.name.localeCompare(b.name);
                  })
                  .map((stat: any) => (
                  <tr
                    key={stat.email}
                    className="dashboard-row-clickable"
                    role="button"
                    tabIndex={0}
                    title={`View ${stat.name}'s client-lane tasks`}
                    // Legacy behaviour: clicking a team member row jumped to
                    // the (single, unified) task list pre-filtered to them.
                    // There's no single unified list to jump to post-rebuild
                    // -- lands on the Client lane (the most common assignee
                    // view) with the same `assigned:` operator the omnibox
                    // already supports on every lane.
                    onClick={() => navigate(`/tasks/client?assigned=${encodeURIComponent(stat.name)}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/tasks/client?assigned=${encodeURIComponent(stat.name)}`);
                    }}
                  >
                    <td style={{ fontWeight: 500 }}>{stat.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="dashboard-badge dashboard-badge-pending">{stat.pending}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="dashboard-badge dashboard-badge-assigned">{stat.assignedToClaim}</span>
                    </td>
                    {dateRangeApplied ? (
                      <td style={{ textAlign: 'center' }}>
                        <span className="dashboard-badge dashboard-badge-completed">{stat.completedInRange || 0}</span>
                      </td>
                    ) : (
                      <>
                        <td style={{ textAlign: 'center' }}>
                          <span className="dashboard-badge dashboard-badge-completed">{stat.completedWeek}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{stat.completedMonth}</td>
                        <td style={{ textAlign: 'center' }}>{stat.completedAllTime}</td>
                      </>
                    )}
                  </tr>
                ))}
                {dashboardStats.aiStats && (
                  <tr className="dashboard-row-ai">
                    <td style={{ fontWeight: 600 }} title="Auto-completed + auto-archived by the CAF AI processor">
                      AI{' '}
                      <span style={{ fontSize: '10px', background: '#ccfbf1', color: '#0f766e', borderRadius: '999px', padding: '2px 8px', fontWeight: 700, verticalAlign: 'middle' }}>
                        CAF PROCESSOR
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', color: '#94a3b8' }}>—</td>
                    <td style={{ textAlign: 'center', color: '#94a3b8' }}>—</td>
                    {dateRangeApplied ? (
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{dashboardStats.aiStats.completedInRange ?? 0}</td>
                    ) : (
                      <>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{dashboardStats.aiStats.completedWeek ?? 0}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{dashboardStats.aiStats.completedMonth ?? 0}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{dashboardStats.aiStats.completedAll ?? 0}</td>
                      </>
                    )}
                  </tr>
                )}
                <tr className="dashboard-row-unassigned">
                  <td style={{ fontWeight: 600 }}>Unassigned</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="dashboard-badge dashboard-badge-unassigned">{dashboardStats.unassigned}</span>
                  </td>
                  <td colSpan={dateRangeApplied ? 1 : 4} style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    —
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Summary cards */}
          <div className="dashboard-summary-cards" style={{ marginTop: '16px' }}>
            <div className="dashboard-card">
              <div className="dashboard-card-value" style={{ color: '#dc2626' }}>
                {dashboardStats.unassigned}
              </div>
              <div className="dashboard-card-label">Unassigned</div>
            </div>
            <div className="dashboard-card">
              <div className="dashboard-card-value" style={{ color: '#f59e0b' }}>
                {dashboardStats.stats.reduce((sum: number, s: any) => sum + s.pending, 0)}
              </div>
              <div className="dashboard-card-label">Total Pending</div>
            </div>
            <div className="dashboard-card">
              <div className="dashboard-card-value" style={{ color: '#3b82f6' }}>
                {dashboardStats.stats.reduce((sum: number, s: any) => sum + s.assignedToClaim, 0)}
              </div>
              <div className="dashboard-card-label">Assigned to Claim</div>
            </div>
            <div className="dashboard-card">
              <div className="dashboard-card-value" style={{ color: '#10b981' }}>
                {dateRangeApplied
                  ? dashboardStats.stats.reduce((sum: number, s: any) => sum + (s.completedInRange || 0), 0)
                  : dashboardStats.stats.reduce((sum: number, s: any) => sum + s.completedWeek, 0)}
              </div>
              <div className="dashboard-card-label">
                {dateRangeApplied ? 'Completed (Range)' : 'Completed This Week'}
              </div>
            </div>
          </div>

          {/* Type Breakdown Charts */}
          {dashboardStats.typeBreakdown && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              {/* Bar chart: pending vs completed by type */}
              <div style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#334155' }}>Tasks by Type</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardStats.typeBreakdown} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="pending" name="Pending" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Pie chart: pending breakdown by type */}
              <div style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#334155' }}>Pending by Type</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardStats.typeBreakdown.filter((t: any) => t.pending > 0)}
                      dataKey="pending"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ type, pending }: any) => `${type}: ${pending.toLocaleString()}`}
                      labelLine={true}
                    >
                      {dashboardStats.typeBreakdown.filter((t: any) => t.pending > 0).map((_: any, index: number) => (
                        <Cell key={index} fill={['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6'][index % 6]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
